/**
 * EmployeeDataTab — F12.M Etap D Part 2 (Employees fullscreen, Etap 1.3).
 *
 * Silnik formatek (keystone unifikacja-formatek, ostatni krok migracji — mirror
 * `ContractorDataTab`/`UserDataTab`): pola core routowane przez
 * `renderField(fieldId, settings)` z propsów, dostarczany przez rodzica
 * (`EmployeeDetail`) z `useKartotekaRenderField(employeeEngineConfig, runtime)`. Ten
 * komponent pobiera TYLKO aktywny layout modułu Pracownicy przez
 * `useSystemModuleActiveLayout('employees')` (ten sam hook co rodzic — honoruje layout
 * przypięty w pozycji menu `?mi=`) i iteruje po sekcjach z
 * `layout_config.tabs[0].sections` (zakładka „Dane" type='fields') — dla każdego
 * elementu typu 'field' deleguje do `renderField` (silnik sam rozstrzyga core-vs-ext,
 * metadane pola, gcrecord, custom widget vs natywna delegacja) — DataTab nie zna już
 * `SystemModuleField`/`fieldsQ`/`EMPLOYEE_CORE_RENDERERS`.
 *
 * 🔴 Hardkoded sekcja „Dane wrażliwe" (PESEL + numer dowodu) — PII szyfrowane (pgcrypto),
 * CELOWO ABSENT z katalogu `system_module_fields` (patrz `apps/api/lib/seed-system-modules.ts`
 * moduł `employees`) i z server-side denylisty fail-closed
 * (`apps/api/lib/dictionaries/sensitive-guard.ts`). Renderowana ZAWSZE, PRZED sekcjami z
 * layoutu, OUTSIDE silnika — `renderField` jej NIE zna (mirror `tags`/`pkd_codes`
 * kontrahenta w `ContractorDataTab.tsx`). Permission gating (maskowanie w widoku) i
 * logika create/edit input pozostają nietknięte — patrz `SensitiveSection` niżej. NIE
 * dodawaj `pesel`/`id_document_number` do `employeeEngineConfig.tsx` ani do
 * `EMPLOYEE_CORE_FIELD_SLUGS` — złamałoby to podwójną bramkę bezpieczeństwa.
 *
 * Reference fields (company_id / department_id / manager_employee_id) → renderery w
 * `employeeEngineConfig.tsx`, zasilane listami z `EmployeeDetail.tsx` przez kanał `extra`
 * (firmy / działy filtrowane po companyId / pracownicy filtrowani po companyId z
 * wykluczeniem currentEmployeeId).
 *
 * PESEL i numer dowodu:
 *  - View mode: SensitiveField (masked lub plain z permission `hr.employees.read_pesel`)
 *  - Edit mode: input (puste = nie zmieniaj; nowa wartość = backend zaszyfruje)
 *
 * Save flow w EmployeeDetail (rodzic):
 *  - JEDEN POST/PATCH — pola core + extValues + additionalTables w tym samym body
 *    (saveAggregate, backend). PESEL/numer dowodu encrypt/decrypt server-side, poza
 *    transakcją silnika — patrz komentarz w EmployeeDetail.tsx.
 */

import { Fragment, type ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { EmployeeIdDocumentType, EmploymentType } from '@saas/shared';
import { Lock } from 'lucide-react';

import { tryRenderLayoutElement } from '@/components/fields/LayoutElementRegistry';
import { type ContextRenderers, PLACEHOLDER_TYPES } from '@/components/form/RecursiveFormRenderer';
import { isFieldElementVisible, StyledFieldSlot } from '@/components/form/styled-field';
import { Field, Section } from '@/components/kartoteki/CoreFieldLayout';
import { Skeleton } from '@/components/Skeleton';
import { useSystemModuleActiveLayout } from '@/hooks/useSystemModuleActiveLayout';
import type { EmployeeEffectiveSupervisor } from '@/lib/use-tenant-people';
import { cn } from '@/lib/utils';
import { loadTabsFromConfig } from '@/pages/system-modules/layout-shape-adapter';

const MODULE_SLUG = 'employees';

// ─── Type exports (używane w EmployeeDetail) ────────────────────────────────

export interface EmployeeFormData {
  firstName: string;
  lastName: string;
  middleName: string | null;
  birthDate: string | null;
  /** Plain text PESEL — only set in edit mode (empty = no change). */
  pesel: string | null;
  taxId: string | null;
  idDocumentType: EmployeeIdDocumentType | null;
  /** Plain text id document number — only set in edit mode. */
  idDocumentNumber: string | null;
  companyId: string;
  orgUnitId: string | null;
  /** Kod pozycji słownika `employee_positions` (F24 Pakiet 0, dawniej wolny tekst `position`). */
  positionCode: string | null;
  specialization: string | null;
  employmentType: EmploymentType | null;
  employmentStartDate: string | null;
  employmentEndDate: string | null;
  internalCode: string | null;
  /** Nadpisanie przełożonego (wyjątek od drzewa jednostek) — puste = wynika z jednostki. */
  managerEmployeeId: string | null;
  phone: string | null;
  mobile: string | null;
  workEmail: string | null;
  isActive: boolean;
  /**
   * Grupy rekordu — kody słownika `employee_groups`, WIELE na rekord. Służą filtrowaniu
   * listy.
   */
  groups: string[];
  /**
   * Integracja Symfonia (F18/FK) — READ-ONLY pola z synchronizacji (pozycja + status po stronie FK).
   * Per firma (rekord pracownika jest per firma). Hydratowane z detalu, nigdy nie wysyłane w PATCH.
   */
  /** ID rekordu pracownika po stronie Symfonii FK (external_id). READ-ONLY, per firma. */
  symfoniaExternalId: string | null;
  symfoniaPosition: number | null;
  symfoniaActive: boolean | null;
  /**
   * Kategoria rekordu — pojedynczy wybór z `record_categories` (kartoteka_key='employees'),
   * migracja 0128. NIE mylić z grupami (wiele, słownik).
   */
  categoryId: string | null;
}

export const EMPTY_EMPLOYEE_FORM: EmployeeFormData = {
  firstName: '',
  lastName: '',
  middleName: null,
  birthDate: null,
  pesel: null,
  taxId: null,
  idDocumentType: null,
  idDocumentNumber: null,
  companyId: '',
  orgUnitId: null,
  positionCode: null,
  specialization: null,
  employmentType: null,
  employmentStartDate: null,
  employmentEndDate: null,
  internalCode: null,
  managerEmployeeId: null,
  phone: null,
  mobile: null,
  workEmail: null,
  isActive: true,
  groups: [],
  symfoniaExternalId: null,
  symfoniaPosition: null,
  symfoniaActive: null,
  categoryId: null,
};

/**
 * F22 (widoki dodatkowe) — słownik slug→stateKey dla pól core pracownika, mirror
 * `CONTRACTOR_CORE_FIELD_SLUGS` (`components/contractors/ContractorDataTab.tsx`) /
 * `KartotekaEngineConfig.fields` dokumentów. Slugi MUSZĄ pokrywać się z `coreFields` w
 * `apps/api/lib/seed-system-modules.ts` (moduł `employees`) — te same slugi, które
 * `useFieldsForModule('crm_core.employees')` oferuje jako kandydatów na wiązanie
 * `record_field` w edytorze widoku dodatkowego. `pesel`/`idDocumentNumber` CELOWO pominięte —
 * PII szyfrowane, brak odpowiednika w `system_module_fields` (nie są oferowane jako kandydaci;
 * serwer dodatkowo blokuje je czarną listą, PHASE_22_PLAN.md §8 Decyzja 6).
 *
 * Ta mapa jest TERAZ też jedynym źródłem, z którego `employeeEngineConfig.tsx` buduje
 * `KartotekaEngineConfig.fields` (silnik formatek) — stąd `groups` MUSI tu być, mimo że
 * przed migracją `EMPLOYEE_CORE_FIELD_SLUGS` go nie miało (dispatch renderowania szedł
 * wtedy po osobnym rejestrze `EMPLOYEE_CORE_RENDERERS`/`hasEmployeeCoreRenderer`, nie po
 * tej mapie) — mirror identycznego dopisania w `CONTRACTOR_CORE_FIELD_SLUGS`.
 */
export const EMPLOYEE_CORE_FIELD_SLUGS: Record<string, keyof EmployeeFormData> = {
  first_name: 'firstName',
  last_name: 'lastName',
  middle_name: 'middleName',
  birth_date: 'birthDate',
  tax_id: 'taxId',
  id_document_type: 'idDocumentType',
  company_id: 'companyId',
  org_unit_id: 'orgUnitId',
  position_code: 'positionCode',
  specialization: 'specialization',
  employment_type: 'employmentType',
  employment_start_date: 'employmentStartDate',
  employment_end_date: 'employmentEndDate',
  internal_code: 'internalCode',
  manager_employee_id: 'managerEmployeeId',
  phone: 'phone',
  mobile: 'mobile',
  work_email: 'workEmail',
  is_active: 'isActive',
  symfonia_external_id: 'symfoniaExternalId',
  symfonia_position: 'symfoniaPosition',
  symfonia_active: 'symfoniaActive',
  // Grupy ze słownika — pole systemowe `groups` (TEXT[] kodów). W mapie, bo silnik formatek
  // buduje zestaw pól WŁAŚNIE z niej (employeeEngineConfig.tsx); bez wpisu renderer grup
  // nigdy by się nie wywołał (amber fallback "unsupported field").
  groups: 'groups',
  // Kategoria — pole systemowe `category_id` (realna tabela `record_categories`). Analogicznie
  // do `groups`: bez wpisu tutaj `categoryRenderer` nigdy by się nie wywołał.
  category_id: 'categoryId',
};

/**
 * Buduje `Record<slug, wartość>` z aktualnego formularza pracownika — źródło
 * `coreRecordValues` dla `buildAdditionalViewRecordValues`, wołane z `EmployeeDetail.tsx` obok
 * `extValues`. Zwraca `unknown` (nie od razu JSON-safe) — koercja jest zadaniem
 * `buildAdditionalViewRecordValues`, nie tej funkcji.
 */
export function buildEmployeeCoreRecordValues(form: EmployeeFormData): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [slug, key] of Object.entries(EMPLOYEE_CORE_FIELD_SLUGS)) {
    result[slug] = form[key];
  }
  return result;
}

// ─── Types dla fetch'owanego layoutu ─────────────────────────────────────────

interface LayoutElement {
  id: string;
  type: string;
  fieldId?: string;
  /** Ukrycie przez nakładkę per-tenant (systemowy layout) — pomijane na karcie. */
  hidden?: boolean;
  settings?: Record<string, unknown>;
}
interface LayoutSection {
  id: string;
  title: string | null;
  columns?: number;
  elements: LayoutElement[];
}

// ─── Props ─────────────────────────────────────────────────────────────────

export interface EmployeeDataTabProps {
  form: EmployeeFormData;
  onChange: (next: EmployeeFormData) => void;
  editable: boolean;
  isCreate: boolean;
  /** Czy PESEL w odpowiedzi był zamaskowany (brak permission read_pesel). */
  peselMasked: boolean;
  /** Czy numer dowodu w odpowiedzi był zamaskowany. */
  idDocumentMasked: boolean;
  /**
   * Renderery elementów zależnych od kontekstu (record_links_view/attachments_view/…),
   * te same, które EmployeeDetail przekazuje do RecursiveFormRenderer. Bez nich taki
   * element postawiony w zakładce „Dane” renderuje się jako martwy placeholder
   * z generycznego rejestru. Most do czasu przejścia kartoteki w całości na silnik.
   */
  contextRenderers?: ContextRenderers;
  /**
   * Renderuje pole core/ext po id — dostarczany przez rodzica z
   * `useKartotekaRenderField(employeeEngineConfig, runtime)`. Silnik sam rozstrzyga
   * core-vs-ext, metadane pola (gcrecord/isCore/isRequired) i custom widget vs natywną
   * delegację — ten komponent tylko iteruje po layoutcie.
   */
  renderField: (fieldId: string, settings?: Record<string, unknown>) => ReactNode;
  /**
   * Wartości formularza keyed po slugu pola (core + ext) — do oceny warunków
   * widoczności pól/bloków (`settings.conditionalField/Operator/Value`). Rodzic
   * (`EmployeeDetail`) buduje ją z `buildEmployeeCoreRecordValues(form)` + `extValues`.
   * Default `{}` — bez niej warunki traktujemy jak „zawsze widoczne".
   */
  formData?: Record<string, unknown>;
  /** Czy pole SensitiveSection (pesel/idDocumentNumber) ma serwerowy błąd walidacji. */
  invalid?: (field: string) => boolean;
  /** Komunikat błędu walidacji pod polem SensitiveSection — `useFieldErrors`. */
  error?: (field: string) => string | undefined;
  /**
   * Przełożony WYNIKOWY (F24 Pakiet 0) — czytany z `EmployeeDetail.effectiveSupervisor`,
   * OPCJONALNY (backend może jeszcze nie liczyć tego pola — równoległy task). Blok
   * read-only renderowany TYLKO gdy podane (klucz obecny w odpowiedzi), więc karta
   * działa identycznie przed i po wdrożeniu tamtej zmiany. `undefined` = nie renderuj;
   * `null` = pole obecne w odpowiedzi, ale pracownik nie ma przełożonego (root drzewa).
   */
  effectiveSupervisor?: EmployeeEffectiveSupervisor | null;
}

// ─── Główny komponent ──────────────────────────────────────────────────────

export function EmployeeDataTab({
  form,
  onChange,
  editable,
  isCreate,
  peselMasked,
  idDocumentMasked,
  contextRenderers,
  renderField,
  formData = {},
  invalid,
  error,
  effectiveSupervisor,
}: EmployeeDataTabProps) {
  const { t } = useTranslation();

  // Aktywny layout — TEN SAM hook co rodzic: honoruje layout przypięty w pozycji menu
  // (`?mi=` → `layoutId`) i sam nakłada nakładkę per-tenant (`applyTabOverrides`).
  // Metadane pól żyją w silniku (useKartotekaRenderField, rodzic), ten komponent zna
  // tylko strukturę sekcji.
  const layoutQ = useSystemModuleActiveLayout(MODULE_SLUG);

  const { dataTabSections, dataTabExtras } = useMemo(() => {
    // F12.N post-E1.T7: backend zwraca NOWY rekurencyjny kształt
    // (`elements[0].tabs[].children`). Adapter `loadTabsFromConfig` rozpakowuje
    // do starego płaskiego `LayoutTab[]` z polami `sections` + `extras`. Nakładkę
    // per-tenant (ukryte pola/formatki) nakłada już hook — tu NIE aplikujemy jej drugi raz.
    const cfg = layoutQ.layoutConfig ?? { elements: [] };
    const tabs = loadTabsFromConfig(cfg);
    const dataTab = tabs.find((tab) => tab.type === 'fields');
    return {
      dataTabSections: (dataTab?.sections ?? []) as LayoutSection[],
      // Luźne elementy zakładki Dane spoza sekcji — bez tego adapter chowa je w `extras`
      // i strona milcząco je gubi (mirror ContractorDataTab/CompanyDataTab).
      dataTabExtras: (dataTab?.extras ?? []) as LayoutElement[],
    };
  }, [layoutQ.layoutConfig]);

  // Renderuje „luźny" element layoutu (blok-widok spoza sekcji) PEŁNĄ szerokością: przez
  // contextRenderers rodzica, a dla elementów bez kontekstu — przez generyczny rejestr.
  function renderLooseElement(el: LayoutElement): ReactNode {
    const contextRenderer = PLACEHOLDER_TYPES.has(el.type)
      ? contextRenderers?.[el.type]
      : undefined;
    if (contextRenderer) {
      return (
        <Fragment key={el.id}>
          {contextRenderer({ element: { id: el.id, type: el.type, settings: el.settings } })}
        </Fragment>
      );
    }
    const layoutNode = tryRenderLayoutElement({
      element: {
        id: el.id,
        type: el.type,
        settings: (el.settings ?? {}) as Record<string, unknown>,
      },
      children: [],
      mode: 'form',
    });
    return layoutNode ? <Fragment key={el.id}>{layoutNode}</Fragment> : null;
  }

  function set<K extends keyof EmployeeFormData>(key: K, value: EmployeeFormData[K]) {
    onChange({ ...form, [key]: value });
  }

  if (layoutQ.isLoading) {
    return (
      <div className="space-y-3 p-4" data-testid="employee-data-tab-loading">
        <Skeleton className="h-32" />
        <Skeleton className="h-32" />
        <Skeleton className="h-32" />
      </div>
    );
  }

  const showLayoutWarning = layoutQ.isError || (!layoutQ.isLoading && dataTabSections.length === 0);

  return (
    <div className="space-y-6" data-testid="employee-data-tab">
      {showLayoutWarning && (
        <div
          className="rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-700 dark:bg-amber-900/20 dark:text-amber-300"
          data-testid="employee-data-tab-no-layout"
        >
          {t('tenant.employees.detail.noLayoutWarning')}
        </div>
      )}

      {/* Dane wrażliwe — hardkoded, ZAWSZE widoczne (PESEL + numer dowodu), OUTSIDE silnika
        (renderField jej NIE zna — patrz komentarz nagłówka pliku). */}
      <SensitiveSection
        form={form}
        set={set}
        editable={editable}
        isCreate={isCreate}
        peselMasked={peselMasked}
        idDocumentMasked={idDocumentMasked}
        invalid={invalid}
        error={error}
      />

      {/* Przełożony wynikowy (F24 Pakiet 0) — read-only, OUTSIDE silnika (nie ma
        odpowiednika w katalogu system_module_fields). Renderowany TYLKO gdy backend
        już zwraca `effectiveSupervisor` (klucz obecny — `undefined` = nie renderuj). */}
      {effectiveSupervisor !== undefined && (
        <EffectiveSupervisorSection effectiveSupervisor={effectiveSupervisor} />
      )}

      {dataTabSections.map((section, sectionIdx) => (
        <Fragment key={section.id}>
          <Section title={section.title ?? t('tenant.employees.detail.untitledSection')}>
            {section.elements.map((el) => {
              // Ukrycie przez nakładkę per-tenant (systemowy layout) — pomiń pole/element z `hidden`.
              if (el.hidden === true) return null;
              // Warunek widoczności z kreatora (`settings.conditionalField/Operator/Value`
              // vs `formData`) — parytet z bogatym rendererem.
              if (!isFieldElementVisible(el, formData)) return null;
              // Krok B (2026-05-14) — non-field elementy (heading/divider/alert/...)
              // renderowane przez LayoutElementRegistry. Side-effect import w App.tsx.
              if (el.type !== 'field') {
                // Elementy zależne od kontekstu (record_links_view/attachments_view/…) muszą
                // iść przez contextRenderers rodzica — dokładnie jak w silniku
                // (RecursiveFormRenderer) — inaczej renderują się jako martwy placeholder
                // z generycznego rejestru. Fallback: generyczny rejestr dla elementów bez
                // kontekstu (heading/divider/alert/…).
                const contextRenderer = PLACEHOLDER_TYPES.has(el.type)
                  ? contextRenderers?.[el.type]
                  : undefined;
                if (contextRenderer) {
                  // Blok-widok (powiązania/załączniki/...) to pełnowymiarowa formatka — w gridzie
                  // sekcji musi zająć cały wiersz (`md:col-span-2`), inaczej ściska go do połowy.
                  return (
                    <div key={el.id} className="md:col-span-2">
                      {contextRenderer({
                        element: { id: el.id, type: el.type, settings: el.settings },
                      })}
                    </div>
                  );
                }
                const layoutNode = tryRenderLayoutElement({
                  element: {
                    id: el.id,
                    type: el.type,
                    settings: (el.settings ?? {}) as Record<string, unknown>,
                  },
                  children: [],
                  mode: 'form',
                });
                return layoutNode ? <Fragment key={el.id}>{layoutNode}</Fragment> : null;
              }

              if (!el.fieldId) return null;
              // Styl pola z kreatora (kolor etykiety/kontrolki) — scoped-CSS wokół
              // `renderField`, wspólny klocek z bogatym rendererem (styled-field).
              return (
                <StyledFieldSlot key={el.id} el={el} formData={formData}>
                  {renderField(el.fieldId, el.settings)}
                </StyledFieldSlot>
              );
            })}
          </Section>
          {sectionIdx === 0 && dataTabExtras.map(renderLooseElement)}
        </Fragment>
      ))}
      {dataTabSections.length === 0 && dataTabExtras.map(renderLooseElement)}
    </div>
  );
}

// ─── Sekcja Przełożony wynikowy (F24 Pakiet 0) ──────────────────────────────

function EffectiveSupervisorSection({
  effectiveSupervisor,
}: {
  effectiveSupervisor: EmployeeEffectiveSupervisor | null;
}) {
  const { t } = useTranslation();
  return (
    <div
      className="rounded-lg border border-border-secondary bg-bg-secondary px-4 py-3 text-sm"
      data-testid="employee-effective-supervisor"
    >
      <span className="text-text-tertiary">
        {t('tenant.employees.field.effectiveSupervisor')}:{' '}
      </span>
      {effectiveSupervisor ? (
        <span className="text-text-primary">
          {effectiveSupervisor.fullName}{' '}
          <span className="text-text-tertiary">
            (
            {t(
              effectiveSupervisor.source === 'override'
                ? 'tenant.employees.field.effectiveSupervisorSourceOverride'
                : 'tenant.employees.field.effectiveSupervisorSourceUnit',
            )}
            )
          </span>
        </span>
      ) : (
        <span className="text-text-tertiary">{t('common.notSet')}</span>
      )}
    </div>
  );
}

// ─── Sekcja Dane wrażliwe (PESEL + numer dowodu) ────────────────────────────

function SensitiveSection({
  form,
  set,
  editable,
  isCreate,
  peselMasked,
  idDocumentMasked,
  invalid,
  error,
}: {
  form: EmployeeFormData;
  set: <K extends keyof EmployeeFormData>(k: K, v: EmployeeFormData[K]) => void;
  editable: boolean;
  isCreate: boolean;
  peselMasked: boolean;
  idDocumentMasked: boolean;
  invalid?: (field: string) => boolean;
  error?: (field: string) => string | undefined;
}) {
  const { t } = useTranslation();
  const inputClass = (field: string) =>
    cn(
      'w-full rounded-lg border bg-bg-primary px-3 py-1.5 font-mono text-sm',
      invalid?.(field) ? 'border-red-500 ring-1 ring-red-500' : 'border-border-secondary',
    );
  return (
    <div
      // Dane wrażliwe = czerwień z design systemu Workflow 3.0 (red-50 #fef1f0 tło, error-border
      // #fbbdb8, error-text #b42318; radius karty 12px) — decyzja właścicielki 2026-09-23.
      className="rounded-xl border border-[#fbbdb8] bg-[#fef1f0] p-5 dark:border-red-900 dark:bg-red-950/30"
      data-testid="employee-sensitive-section"
    >
      <div className="mb-3 flex items-center gap-2 text-xs font-medium text-[#b42318] dark:text-red-300">
        <Lock size={12} aria-hidden="true" />
        {t('tenant.employees.sensitive.heading')}
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Field label={t('tenant.employees.field.pesel')} error={error?.('pesel')}>
          {editable ? (
            <input
              type="text"
              value={form.pesel ?? ''}
              onChange={(e) => set('pesel', e.target.value || null)}
              placeholder={!isCreate ? t('tenant.employees.sensitive.editPlaceholder') : undefined}
              data-testid="employee-field-pesel"
              className={inputClass('pesel')}
            />
          ) : (
            <ReadonlySensitive
              value={form.pesel}
              masked={peselMasked}
              testId="employee-field-pesel"
            />
          )}
        </Field>
        <Field
          label={t('tenant.employees.field.idDocumentNumber')}
          error={error?.('idDocumentNumber')}
        >
          {editable ? (
            <input
              type="text"
              value={form.idDocumentNumber ?? ''}
              onChange={(e) => set('idDocumentNumber', e.target.value || null)}
              placeholder={!isCreate ? t('tenant.employees.sensitive.editPlaceholder') : undefined}
              data-testid="employee-field-idDocumentNumber"
              className={inputClass('idDocumentNumber')}
            />
          ) : (
            <ReadonlySensitive
              value={form.idDocumentNumber}
              masked={idDocumentMasked}
              testId="employee-field-idDocumentNumber"
            />
          )}
        </Field>
      </div>
      {(peselMasked || idDocumentMasked) && !editable && (
        <p className="mt-3 text-xs text-[#b42318] dark:text-red-300">
          {t('tenant.employees.sensitive.maskedHint')}
        </p>
      )}
    </div>
  );
}

function ReadonlySensitive({
  value,
  masked,
  testId,
}: {
  value: string | null;
  masked: boolean;
  testId?: string;
}) {
  const { t } = useTranslation();
  return (
    <div
      className={`rounded-lg border border-transparent bg-bg-secondary px-3 py-1.5 text-sm font-mono ${
        value === null ? 'text-text-tertiary' : masked ? 'text-text-tertiary' : 'text-text-primary'
      }`}
      data-testid={testId}
    >
      {value ?? t('tenant.employees.sensitive.notSet')}
    </div>
  );
}
